import { Switch } from '../../components/ui/switch';
import SubjectTabs from '../generic/SubjectTabs';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import StatCell from '../generic/StatCell';
import TableHeader from '../../components/generic/TableHeader';
import { PercentIcon } from '@phosphor-icons/react';
import { CheckCircleIcon, XCircleIcon, TimerIcon, SmileyXEyesIcon } from '@phosphor-icons/react';
import NoData from '../generic/NoData';
import { useNavigate } from 'react-router-dom';

const QualityTestBreakdown = ({ data, loading, activeSubject, onSubjectChange, subjects, onResetFilters }) => {
  const [showPercentage, setShowPercentage] = useState(true);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!data || data.length === 0) return;

    // Extract unique subjects from the data
    const subjectsFromData = [];
    const seenSubjects = new Set();

    data.forEach((test) => {
      test.detailedAnalysis?.subjectWise?.forEach((subject) => {
        if (!seenSubjects.has(subject.subjectMeta._id)) {
          seenSubjects.add(subject.subjectMeta._id);
          subjectsFromData.push({
            _id: subject.subjectMeta._id,
            title: subject.subjectMeta.title,
            icon: subject.subjectMeta.icon,
            color: subject.subjectMeta.color,
          });
        }
      });
    });

    // Use the passed subjects if available, otherwise use subjects from data
    const finalSubjects = subjects && subjects.length > 0 ? subjects : subjectsFromData;
    setAvailableSubjects(finalSubjects);
  }, [data, subjects]);

  if (loading) {
    return (
      <div className="border border-[var(--q3-stroke-light)] rounded-lg p-4">
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return <NoData onResetAll={onResetFilters} />;
  }

  return (
    <div>
      <TableHeader
        headerTitle="Test-wise Breakdown"
        showBottomBorder={false}
        headerSwitch={
          <span className="flex items-center gap-1">
            <PercentIcon size={16} weight="bold" />
            Show Percentage
          </span>
        }
        headerSwitchCase={showPercentage}
        headerSwitchHandler={setShowPercentage}
      />

      {/* SubjectTabs with its own horizontal scroll container */}
      <div className="overflow-x-auto border-r border-l border-[var(--q3-stroke-light)] bg-white mb-[-1px]">
        <div className="min-w-0 md:min-w-0">
          <SubjectTabs activeSubject={activeSubject} onSubjectChange={onSubjectChange} subjects={availableSubjects} />
        </div>
      </div>

      {/* Data table content scrolls independently */}
      <div className="overflow-x-auto">
        <div className="min-w-[600px] md:min-w-0 border-r border-l border-b border-[var(--q3-stroke-light)] rounded-b-lg overflow-hidden">
          {/* Header */}
          <div className="bg-[var(--q3-surface-dim)] border-b border-[var(--q3-stroke-light)]">
            <div className="grid grid-cols-5 divide-x divide-[var(--q3-stroke-light)] text-sm font-medium text-[var(--q3-neutral-default)]">
              <div className="p-4">Test</div>

              <div className="p-4 text-center text-[var(--q3-green-normal)]">
                <div className="flex items-center justify-center gap-2">
                  <CheckCircleIcon className="w-5 h-5" />
                  <span>Perfect</span>
                </div>
              </div>

              <div className="p-4 text-center text-[var(--q3-danger-normal)]">
                <div className="flex items-center justify-center gap-2">
                  <XCircleIcon className="w-5 h-5" />
                  <span>Wasted</span>
                </div>
              </div>

              <div className="p-4 text-center text-[var(--q3-orange-normal)]">
                <div className="flex items-center justify-center gap-2">
                  <TimerIcon className="w-5 h-5" />
                  <span>Overtime</span>
                </div>
              </div>

              <div className="p-4 text-center text-[var(--q3-neutral-light)]">
                <div className="flex items-center justify-center gap-2">
                  <SmileyXEyesIcon className="w-5 h-5" />
                  <span>Confused</span>
                </div>
              </div>
            </div>
          </div>

          {/* Table rows */}
          {data.map((test, index) => {
            const subjectData = test.detailedAnalysis?.subjectWise?.find(
              (sub) => sub.subjectMeta._id === activeSubject,
            );

            if (!subjectData) return null;

            return (
              <div key={index} className="border-b border-[var(--q3-stroke-light)] last:border-b-0">
                <div className="grid grid-cols-5 divide-x divide-[var(--q3-stroke-light)] text-sm text-[var(--q3-neutral-default)]">
                  {/* Test Info (Clickable) */}
                  <div
                    className="p-4 cursor-pointer hover:bg-[var(--q3-surface-hover)]"
                    onClick={() => navigate(`/${test.packId}/${test.testId}/analysis`)}
                  >
                    <div className="font-medium text-[var(--q3-neutral-default)] truncate max-w-[180px]">
                      {test.detailedAnalysis.testTitle}
                    </div>
                    <div className="text-xs text-[var(--q3-neutral-light)]">
                      {moment(test.detailedAnalysis.submittedAt).format('D MMM YYYY')}
                      {test.detailedAnalysis.isBonusAvailable && test.detailedAnalysis.isBonusAttempted && ' | Bonus'}
                    </div>
                  </div>

                  {/* Stat Cells */}
                  <div className="text-center">
                    <StatCell
                      color="var(--q3-green-normal)"
                      top={subjectData.perfectAttempts}
                      bottom={subjectData.totalQuestions}
                      showPercentage={showPercentage}
                      compact={true}
                    />
                  </div>

                  <div className="text-center">
                    <StatCell
                      color="var(--q3-danger-normal)"
                      top={subjectData.wastedAttempts}
                      bottom={subjectData.totalQuestions}
                      showPercentage={showPercentage}
                      compact={true}
                    />
                  </div>

                  <div className="text-center">
                    <StatCell
                      color="var(--q3-orange-normal)"
                      top={subjectData.overtimeAttempts}
                      bottom={subjectData.totalQuestions}
                      showPercentage={showPercentage}
                      compact={true}
                    />
                  </div>

                  <div className="text-center">
                    <StatCell
                      color="var(--q3-neutral-light)"
                      top={subjectData.confusedAttempts}
                      bottom={subjectData.totalQuestions}
                      showPercentage={showPercentage}
                      themedText
                      compact={true}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

QualityTestBreakdown.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  activeSubject: PropTypes.string,
  onSubjectChange: PropTypes.func,
  subjects: PropTypes.array,
  onResetFilters: PropTypes.func,
};

export default QualityTestBreakdown;
